import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ToolLayout from 'tools/ToolLayout';
import FileDropZone from 'tools/FileDropZone';
import Colors from 'theme/colors';
import {
  btnDisabled,
  btnRow,
  card,
  chipScrollRow,
  chipStyle,
  dangerBtn,
  errorBox,
  hint,
  label,
  primaryBtn,
  privacy,
  secondaryBtn,
  textArea,
  touchBtnFull,
  warnBox,
} from 'tools/toolStyles';
import { MODELS, ModelEntry, getModelById } from './modelRegistry';
import { HardwareInfo, detectHardware, tierRank } from './hardware';
import { getEngineForModel } from './engines';
import { PresetVoice } from './engines/types';
import {
  ClonedVoice,
  deleteClonedVoice,
  listClonedVoices,
  newVoiceId,
  saveClonedVoice,
} from './voiceStore';
import {
  createMicRecorder,
  decodeToMonoPcm,
  downloadBlob,
  encodeWav,
  revokeUrl,
} from './audioUtils';
import { finalizeAudio } from './postfx';
import { downloadBlob as ffmpegDownload, runFFmpegJob, replaceExt } from 'tools/ffmpegShared';

const SUGGESTIONS_EN = [
  'Hello! Welcome to Language123.',
  'Please speak clearly and slowly.',
  'The meeting starts at nine o\'clock tomorrow morning.',
  'I would like a cup of coffee, please.',
  'Practice every day and you will improve.',
];

const SUGGESTIONS_VI = [
  'Xin chào! Chào mừng bạn đến Language123.',
  'Hãy nói chậm và rõ ràng.',
  'Cuộc họp bắt đầu lúc chín giờ sáng mai.',
  'Tôi muốn một tách cà phê.',
  'Luyện tập mỗi ngày bạn sẽ tiến bộ.',
];

const sliderRow: any = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 10,
};

const sliderLabel: any = {
  minWidth: 90,
  fontSize: 13,
  fontWeight: 600,
};

const rangeInput: any = {
  flex: 1,
  minWidth: 0,
};

const badgeBase: any = {
  display: 'inline-block',
  fontSize: 11,
  fontWeight: 700,
  padding: '2px 8px',
  borderRadius: 999,
  marginLeft: 6,
  verticalAlign: 'middle',
};

function modelBadge(kind: 'recommended' | 'heavy' | 'soon' | 'ok' | 'beta'): any {
  if (kind === 'recommended') {
    return { ...badgeBase, background: '#e8f5e9', color: '#1b5e20' };
  }
  if (kind === 'heavy') {
    return { ...badgeBase, background: '#fff3e0', color: '#e65100' };
  }
  if (kind === 'soon') {
    return { ...badgeBase, background: '#eceff1', color: '#546e7a' };
  }
  if (kind === 'beta') {
    return { ...badgeBase, background: '#fff8e1', color: '#f57f17' };
  }
  return { ...badgeBase, background: '#e3f2fd', color: '#1565c0' };
}

const TextToSpeech = () => {
  const [narrow, setNarrow] = useState(false);
  const [hw, setHw] = useState<HardwareInfo | null>(null);
  const [modelId, setModelId] = useState('webspeech');
  const [voices, setVoices] = useState<PresetVoice[]>([]);
  const [voiceId, setVoiceId] = useState('');
  const [voiceTab, setVoiceTab] = useState<'preset' | 'clone'>('preset');
  const [cloned, setCloned] = useState<ClonedVoice[]>([]);
  const [selectedCloneId, setSelectedCloneId] = useState('');
  const [cloneName, setCloneName] = useState('');
  const [text, setText] = useState(SUGGESTIONS_VI[0]);
  const [langHint, setLangHint] = useState<'en' | 'vi'>('vi');
  const [speed, setSpeed] = useState(1);
  const [pitch, setPitch] = useState(0);
  const [exaggeration, setExaggeration] = useState(0.5);
  const [status, setStatus] = useState('');
  const [loadPct, setLoadPct] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [lastPcm, setLastPcm] = useState<Float32Array | null>(null);
  const [lastRate, setLastRate] = useState(24000);
  const [recState, setRecState] = useState<'idle' | 'recording'>('idle');
  const [recElapsed, setRecElapsed] = useState(0);
  const [consentOk, setConsentOk] = useState(false);
  const [diskCached, setDiskCached] = useState(false);

  const recorderRef = useRef<ReturnType<typeof createMicRecorder> | null>(null);
  const tickRef = useRef<any>(null);
  const abortRef = useRef({ aborted: false });
  const audioRef = useRef<any>(null);

  const model = useMemo(() => getModelById(modelId) as ModelEntry, [modelId]);
  const engine = useMemo(() => getEngineForModel(model), [model]);

  /** Clone model depends on language: VieNeu for VI, Chatterbox for EN */
  const cloneModelId = langHint === 'vi' ? 'vieneu-v3-turbo' : 'chatterbox-en';
  const cloneModel = useMemo(
    () => getModelById(cloneModelId) as ModelEntry,
    [cloneModelId]
  );
  const cloneEngine = useMemo(() => getEngineForModel(cloneModel), [cloneModel]);

  /** Active engine for current action: clone tab uses language-matched clone model */
  const activeEngine = voiceTab === 'clone' ? cloneEngine : engine;
  const activeModelId = voiceTab === 'clone' ? cloneModelId : modelId;
  const activeModel = voiceTab === 'clone' ? cloneModel : model;

  const enterCloneMode = useCallback(() => {
    setVoiceTab('clone');
    const mid = langHint === 'vi' ? 'vieneu-v3-turbo' : 'chatterbox-en';
    setModelId(mid);
    setError('');
    const m = getModelById(mid);
    setStatus(
      'Chế độ Clone Voice — ' +
        (langHint === 'vi'
          ? 'tiếng Việt dùng VieNeu (~280MB)'
          : 'tiếng Anh dùng Chatterbox (~1.5GB)') +
        '. Model: ' +
        ((m && m.label) || mid) +
        '.'
    );
  }, [langHint]);

  useEffect(() => {
    const g: any = typeof global !== 'undefined' ? global : {};
    const win = g.window || g;
    const measure = () => {
      const w = win.innerWidth != null ? win.innerWidth : 800;
      setNarrow(w < 520);
    };
    measure();
    detectHardware().then(setHw).catch(() => setHw(null));
    listClonedVoices()
      .then((list) => {
        setCloned(list);
        if (list.length && !selectedCloneId) {
          // keep empty until user picks — don't auto-force clone tab
        }
      })
      .catch(() => setCloned([]));
    if (win.addEventListener) {
      win.addEventListener('resize', measure);
      return () => {
        win.removeEventListener('resize', measure);
        if (tickRef.current) clearInterval(tickRef.current);
        revokeUrl(audioUrl);
      };
    }
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      revokeUrl(audioUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    setDiskCached(false);
    const eng = activeEngine;
    if (!eng.hasDiskCache) return;
    eng
      .hasDiskCache(activeModelId)
      .then((ok) => {
        if (!cancelled) {
          setDiskCached(!!ok);
          if (ok) setConsentOk(true);
        }
      })
      .catch(() => {
        if (!cancelled) setDiskCached(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeEngine, activeModelId]);

  const refreshVoices = useCallback(async () => {
    try {
      if (!engine.isReady(modelId) && model.engine === 'webspeech') {
        await engine.ensureLoaded(modelId);
      }
      if (engine.isReady(modelId) || model.engine === 'webspeech') {
        const list = await engine.listVoices(modelId);
        setVoices(list);
        if (list.length && !list.find((v) => v.id === voiceId)) {
          const prefer = list.find((v) => v.lang === langHint) || list[0];
          setVoiceId(prefer.id);
        }
      }
    } catch {
      /* ignore */
    }
  }, [engine, modelId, model.engine, voiceId, langHint]);

  useEffect(() => {
    refreshVoices();
  }, [modelId, refreshVoices]);

  const onProgress = (info: { status: string; progress?: number }) => {
    setStatus(info.status);
    if (typeof info.progress === 'number') setLoadPct(info.progress);
  };

  const loadModel = async () => {
    const target = activeModel;
    const eng = activeEngine;
    const mid = activeModelId;
    if (target.comingSoon) {
      setError('Model này chưa sẵn sàng.');
      return;
    }
    // Auto-accept consent when user explicitly clicks download
    if (target.needsConsent && !consentOk) {
      setConsentOk(true);
    }
    setBusy(true);
    setError('');
    try {
      await eng.ensureLoaded(mid, onProgress);
      await refreshVoices();
      setDiskCached(true);
      setStatus('Model sẵn sàng.');
      setLoadPct(1);
    } catch (err: any) {
      setError((err && err.message) || 'Tải model thất bại.');
      setStatus('');
    } finally {
      setBusy(false);
    }
  };

  const unloadModel = async () => {
    setBusy(true);
    setError('');
    try {
      if (activeEngine.unload) await activeEngine.unload(activeModelId);
      setStatus('Đã bỏ model khỏi RAM — file vẫn giữ trên máy, refresh không cần tải lại.');
      setLoadPct(0);
      // Re-check disk cache for button label
      if (activeEngine.hasDiskCache) {
        const ok = await activeEngine.hasDiskCache(activeModelId).catch(() => false);
        setDiskCached(!!ok);
      }
    } catch (err: any) {
      setError((err && err.message) || 'Không xóa được.');
    } finally {
      setBusy(false);
    }
  };

  const playPcm = (pcm: Float32Array, sampleRate: number) => {
    revokeUrl(audioUrl);
    if (!pcm || !pcm.length) {
      setAudioUrl(null);
      setLastPcm(null);
      return;
    }
    const blob = encodeWav(pcm, sampleRate);
    const g: any = typeof global !== 'undefined' ? global : {};
    const win = g.window || g;
    const url = win.URL.createObjectURL(blob);
    setAudioUrl(url);
    setLastPcm(pcm);
    setLastRate(sampleRate);
    setTimeout(() => {
      if (audioRef.current) {
        try {
          audioRef.current.play();
        } catch {
          /* ignore */
        }
      }
    }, 50);
  };

  const doSynthesize = async () => {
    const t = (text || '').trim();
    if (!t) {
      setError('Hãy nhập văn bản.');
      return;
    }

    const selectedClone = cloned.find((c) => c.id === selectedCloneId);
    const useClone = voiceTab === 'clone' && !!selectedClone;

    if (voiceTab === 'clone' && !selectedClone) {
      setError('Hãy clone một giọng hoặc chọn giọng đã lưu trước khi tổng hợp.');
      return;
    }

    // Prefer the engine that created the cloned voice; else language-matched clone model
    const synthModelId = useClone
      ? selectedClone!.modelId || cloneModelId
      : modelId;
    const synthModel =
      (useClone
        ? getModelById(synthModelId) || cloneModel
        : model) as ModelEntry;
    const synthEngine = useClone
      ? getEngineForModel(synthModel)
      : engine;

    if (synthModel.comingSoon) {
      setError('Model này chưa sẵn sàng.');
      return;
    }

    abortRef.current = { aborted: false };
    setBusy(true);
    setError('');
    setStatus('Đang chuẩn bị…');

    try {
      if (useClone && modelId !== synthModelId) {
        setModelId(synthModelId);
      }
      if (!synthEngine.isReady(synthModelId)) {
        if (synthModel.needsConsent && !consentOk) {
          throw new Error('Hãy xác nhận tải model lớn (~' + synthModel.sizeMb + 'MB) trước.');
        }
        await synthEngine.ensureLoaded(synthModelId, onProgress);
        await refreshVoices();
      }

      if (useClone && selectedClone) {
        if (
          synthModel.engine === 'chatterbox' &&
          (!selectedClone.metaRefPcm || !selectedClone.metaRefPcm.length)
        ) {
          throw new Error('Giọng đã lưu thiếu mẫu âm thanh. Hãy clone lại voice.');
        }
      }

      const result = await synthEngine.synthesize(synthModelId, {
        text: t,
        voiceId: useClone ? undefined : voiceId,
        embedding: useClone && selectedClone ? selectedClone.embedding : undefined,
        referencePcm:
          useClone && selectedClone && selectedClone.metaRefPcm
            ? selectedClone.metaRefPcm
            : undefined,
        referenceSampleRate: useClone && selectedClone ? selectedClone.sampleRate : undefined,
        engineMeta: useClone && selectedClone ? selectedClone.engineMeta : undefined,
        speed,
        pitchSemitones: pitch,
        exaggeration,
        lang: langHint,
        onProgress,
        signal: abortRef.current,
      });

      if (synthModel.engine === 'webspeech') {
        setStatus('Đã phát (giọng trình duyệt — không xuất được file).');
        setAudioUrl(null);
        setLastPcm(null);
        return;
      }

      const finalized = await finalizeAudio(result.pcm, result.sampleRate, {
        speed,
        pitchSemitones: pitch,
        nativeSpeedApplied: !!result.nativeSpeedApplied,
      });
      playPcm(finalized.pcm, finalized.sampleRate);
      setStatus(useClone ? 'Xong (voice clone).' : 'Xong.');
    } catch (err: any) {
      setError((err && err.message) || 'Tổng hợp thất bại.');
      setStatus('');
    } finally {
      setBusy(false);
    }
  };

  const cancelSynth = () => {
    abortRef.current.aborted = true;
    if (activeEngine.cancel) activeEngine.cancel();
    setStatus('Đã hủy.');
    setBusy(false);
  };

  const downloadWav = () => {
    if (!lastPcm || !lastPcm.length) return;
    const blob = encodeWav(lastPcm, lastRate);
    downloadBlob(blob, 'tts-' + Date.now() + '.wav');
  };

  const downloadMp3 = async () => {
    if (!lastPcm || !lastPcm.length) return;
    setBusy(true);
    setError('');
    try {
      const wavBlob = encodeWav(lastPcm, lastRate);
      setStatus('Đang chuyển MP3 (FFmpeg)…');
      const mp3 = await runFFmpegJob(
        wavBlob,
        'in.wav',
        'out.mp3',
        ['-i', 'in.wav', '-codec:a', 'libmp3lame', '-qscale:a', '4', 'out.mp3'],
        'audio/mpeg',
        { onStatus: (m) => setStatus(m) }
      );
      ffmpegDownload(mp3, replaceExt('tts-' + Date.now() + '.wav', 'mp3'));
      setStatus('Đã tải MP3.');
    } catch (err: any) {
      setError((err && err.message) || 'Xuất MP3 thất bại. Hãy tải WAV.');
    } finally {
      setBusy(false);
    }
  };

  const cloneFromPcm = async (pcm: Float32Array, sampleRate: number) => {
    const mid = cloneModelId;
    if (modelId !== mid) {
      setModelId(mid);
    }
    setVoiceTab('clone');

    const name = (cloneName || '').trim() || 'Giọng của tôi';
    setBusy(true);
    setError('');
    try {
      if (cloneModel.needsConsent && !consentOk) {
        setConsentOk(true);
      }
      if (!cloneEngine.isReady(mid)) {
        setStatus('Đang tải model clone…');
        await cloneEngine.ensureLoaded(mid, onProgress);
      }
      if (!cloneEngine.cloneVoice) throw new Error('Engine không hỗ trợ clone.');

      setStatus('Đang mã hóa speaker…');
      const result = await cloneEngine.cloneVoice(mid, pcm, sampleRate, onProgress);
      const previewLen = Math.min(pcm.length, sampleRate * 3);
      const voice: ClonedVoice = {
        id: newVoiceId(),
        name,
        engine: cloneModel.engine,
        modelId: mid,
        lang: langHint,
        sampleRate: result.sampleRate,
        embedding: result.embedding || new Float32Array(0),
        previewPcm: pcm.slice(0, previewLen),
        createdAt: Date.now(),
      };
      if (result.meta && result.meta.referencePcm) {
        voice.metaRefPcm = Float32Array.from(result.meta.referencePcm);
      } else {
        voice.metaRefPcm = pcm instanceof Float32Array ? pcm.slice(0) : new Float32Array(pcm);
      }
      if (result.meta) {
        voice.engineMeta = result.meta;
      }

      setStatus('Đang lưu giọng…');
      await saveClonedVoice(voice);
      const list = await listClonedVoices();
      setCloned(list);
      setSelectedCloneId(voice.id);
      setVoiceTab('clone');
      setCloneName('');
      setStatus('Đã lưu giọng: ' + name + ' — sẵn sàng TTS.');
      setLoadPct(1);
    } catch (err: any) {
      setError((err && err.message) || 'Clone thất bại.');
      setStatus('');
    } finally {
      setBusy(false);
    }
  };

  const onCloneFiles = async (files: any[]) => {
    if (!files || !files.length) return;
    if (cloneModel.needsConsent && !consentOk) {
      setConsentOk(true);
    }
    setBusy(true);
    setError('');
    try {
      setStatus('Đang đọc file âm thanh…');
      const { pcm, sampleRate } = await decodeToMonoPcm(files[0], 24000);
      if (pcm.length < 24000 * 3) {
        throw new Error('File quá ngắn — cần khoảng 5–30 giây giọng nói rõ.');
      }
      await cloneFromPcm(pcm, sampleRate);
    } catch (err: any) {
      setError((err && err.message) || 'Không đọc được file.');
      setBusy(false);
      setStatus('');
    }
  };

  const startRec = async () => {
    setError('');
    // Allow recording immediately — model download happens when cloning
    if (cloneModel.needsConsent && !consentOk) {
      setConsentOk(true);
    }
    try {
      if (!recorderRef.current) recorderRef.current = createMicRecorder(30, 24000);
      await recorderRef.current.start();
      setRecState('recording');
      setRecElapsed(0);
      if (tickRef.current) clearInterval(tickRef.current);
      const started = Date.now();
      tickRef.current = setInterval(() => {
        const sec = Math.floor((Date.now() - started) / 1000);
        setRecElapsed(sec);
        if (sec >= 30) stopRec();
      }, 200);
    } catch (err: any) {
      setError((err && err.message) || 'Không bắt đầu được ghi âm. Hãy cho phép microphone trong trình duyệt.');
      setRecState('idle');
    }
  };

  const stopRec = async () => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    const rec = recorderRef.current;
    setRecState('idle');
    if (!rec) return;
    try {
      const pcm = await rec.stop();
      if (!pcm || pcm.length < 24000 * 3) {
        setError('Mẫu ghi quá ngắn — hãy nói 5–30 giây.');
        return;
      }
      await cloneFromPcm(pcm, 24000);
    } catch (err: any) {
      setError((err && err.message) || 'Dừng ghi thất bại.');
    }
  };

  const removeClone = async (id: string) => {
    await deleteClonedVoice(id);
    const list = await listClonedVoices();
    setCloned(list);
    if (selectedCloneId === id) setSelectedCloneId('');
  };

  const previewClone = (v: ClonedVoice) => {
    if (v.previewPcm && v.previewPcm.length) {
      playPcm(v.previewPcm, v.sampleRate);
    }
  };

  const suggestions = langHint === 'vi' ? SUGGESTIONS_VI : SUGGESTIONS_EN;

  const modelCard = (m: ModelEntry) => {
    const active = m.id === modelId;
    const belowTier = hw && tierRank(hw.tier) < tierRank(m.minTier);
    let badge: any = null;
    if (m.comingSoon) badge = <span style={modelBadge('soon')}>Sắp ra mắt</span>;
    else if (m.engine === 'vieneu') badge = <span style={modelBadge('beta')}>Beta · Clone VI</span>;
    else if (hw && m.recommended && !belowTier && (m.engine === 'webspeech' || m.engine === 'kokoro'))
      badge = <span style={modelBadge('recommended')}>Đề xuất</span>;
    else if (belowTier) badge = <span style={modelBadge('heavy')}>Máy có thể chậm</span>;
    else if (m.cloning) badge = <span style={modelBadge('ok')}>Clone</span>;

    return (
      <button
        key={m.id}
        type="button"
        disabled={!!m.comingSoon}
        style={{
          ...chipStyle(active),
          opacity: m.comingSoon ? 0.55 : 1,
          textAlign: 'left',
          maxWidth: '100%',
          whiteSpace: 'normal',
          lineHeight: 1.35,
          padding: '10px 12px',
        }}
        onClick={() => {
          if (m.comingSoon) return;
          setModelId(m.id);
          setError('');
          setStatus('');
          setLoadPct(0);
          // Clone model follows language (step 3), not the EN-only card highlight
          if (m.cloning && m.langs.indexOf('vi') >= 0) {
            setLangHint('vi');
            setModelId('vieneu-v3-turbo');
          } else if (m.id === 'chatterbox-en') {
            setLangHint('en');
          } else if (m.langs.indexOf('vi') >= 0 && m.langs.indexOf('en') < 0) {
            setLangHint('vi');
          } else if (m.langs.indexOf('en') >= 0 && m.langs.indexOf('vi') < 0) {
            setLangHint('en');
          }
          if (m.cloning && !m.comingSoon) {
            setVoiceTab('clone');
          } else {
            setVoiceTab('preset');
          }
        }}
      >
        <div style={{ fontWeight: 700 }}>
          {m.label}
          {badge}
        </div>
        <div style={{ fontSize: 12, fontWeight: 500, opacity: 0.9, marginTop: 2 }}>{m.subtitle}</div>
        <div style={{ fontSize: 11, marginTop: 4, opacity: 0.8 }}>
          {m.sizeMb > 0 ? '~' + m.sizeMb + 'MB · ' : ''}
          {m.license}
          {m.minTier ? ' · cần: ' + m.minTier : ''}
        </div>
      </button>
    );
  };

  return (
    <ToolLayout title="Text to Speech" showBackToToeic>
      <p style={privacy}>
        Âm thanh và model AI được xử lý hoàn toàn trên máy bạn — không gửi lên server language123.net.
        Chỉ dùng model license mở (MIT / Apache-2.0 / Web Speech).
      </p>
      {hw ? (
        <p style={hint}>
          Phần cứng: <strong>{hw.label}</strong>
          {hw.hasWebGPU ? ' · WebGPU' : ' · không WebGPU'}
          {hw.deviceMemoryGb != null ? ' · ~' + hw.deviceMemoryGb + 'GB RAM' : ''}
          {' · '}
          {hw.cores} lõi
        </p>
      ) : null}

      <div style={card}>
        <label style={label}>1. Chọn model AI</label>
        <div style={{ ...chipScrollRow, flexWrap: 'wrap', overflowX: 'visible' }}>
          {MODELS.map(modelCard)}
        </div>
        {(activeModel.needsConsent || (voiceTab === 'clone' && cloneModel.needsConsent)) ? (
          <label style={{ ...hint, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={consentOk}
              onChange={(e: any) => setConsentOk(!!e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span>
              Tôi hiểu model clone ~{cloneModel.sizeMb || activeModel.sizeMb}MB, tải về máy tôi lần
              đầu và có thể chạy chậm nếu không có WebGPU.
            </span>
          </label>
        ) : null}
        {voiceTab === 'clone' ? (
          <p style={warnBox}>
            <strong>Clone theo ngôn ngữ ở bước 3:</strong> Tiếng Việt → VieNeu (~280MB). Tiếng Anh →
            Chatterbox (~1.5GB, tải rất lâu). Đang chọn:{' '}
            <strong>{langHint === 'vi' ? 'VieNeu' : 'Chatterbox'}</strong>.
          </p>
        ) : null}
        <div style={btnRow}>
          <button
            type="button"
            style={{
              ...(busy || activeModel.comingSoon || activeEngine.isReady(activeModelId)
                ? btnDisabled(secondaryBtn)
                : secondaryBtn),
              ...(narrow ? touchBtnFull : {}),
            }}
            disabled={busy || activeModel.comingSoon || activeEngine.isReady(activeModelId)}
            onClick={loadModel}
          >
            {activeEngine.isReady(activeModelId)
              ? 'Model đã sẵn sàng'
              : diskCached
              ? 'Nạp model (đã tải sẵn)'
              : activeModel.sizeMb
              ? 'Tải model (~' + activeModel.sizeMb + 'MB)'
              : 'Chuẩn bị engine'}
          </button>
          {activeEngine.isReady(activeModelId) && activeModel.engine !== 'webspeech' ? (
            <button
              type="button"
              style={{ ...(busy ? btnDisabled(dangerBtn) : dangerBtn), ...(narrow ? touchBtnFull : {}) }}
              disabled={busy}
              onClick={unloadModel}
            >
              Bỏ model khỏi bộ nhớ
            </button>
          ) : null}
          <button
            type="button"
            style={{
              ...(busy ? btnDisabled(primaryBtn) : primaryBtn),
              ...(narrow ? touchBtnFull : {}),
            }}
            disabled={busy}
            onClick={enterCloneMode}
          >
            Bắt đầu clone voice
          </button>
        </div>
        {diskCached && !activeEngine.isReady(activeModelId) ? (
          <p style={hint}>
            Model đã lưu trên máy từ lần trước — bấm <strong>Nạp model</strong> để đưa vào RAM (không
            tải lại ~{activeModel.sizeMb || '?'}MB).
          </p>
        ) : null}
        {(busy || loadPct > 0) && loadPct < 1 ? (
          <div style={{ height: 8, background: '#eceff1', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
            <div
              style={{
                width: Math.round(loadPct * 100) + '%',
                height: '100%',
                background: Colors.MAIN_COLOR,
                transition: 'width 0.2s',
              }}
            />
          </div>
        ) : null}
        {status ? <p style={hint}>{status}</p> : null}
      </div>

      <div style={card}>
        <label style={label}>2. Giọng nói</label>
        <div style={btnRow}>
          <button
            type="button"
            style={chipStyle(voiceTab === 'preset')}
            onClick={() => {
              setVoiceTab('preset');
              setError('');
            }}
          >
            Có sẵn
          </button>
          <button
            type="button"
            style={chipStyle(voiceTab === 'clone')}
            onClick={enterCloneMode}
            title="Clone voice từ file hoặc micro (VieNeu VI / Chatterbox EN)"
          >
            Của tôi (clone)
          </button>
        </div>

        {voiceTab === 'preset' ? (
          <>
            <p style={hint}>Chọn giọng có sẵn của model. Đổi model ở bước 1 để có thêm giọng EN/VI.</p>
            <div style={{ ...chipScrollRow, flexWrap: 'wrap', overflowX: 'visible' }}>
              {voices.length === 0 ? (
                <span style={hint}>
                  {model.engine === 'webspeech'
                    ? 'Đang lấy danh sách giọng…'
                    : 'Tải model trước để xem giọng.'}
                </span>
              ) : (
                voices.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    style={chipStyle(voiceId === v.id)}
                    onClick={() => {
                      setVoiceId(v.id);
                      if (v.lang === 'vi' || v.lang === 'en') setLangHint(v.lang as 'en' | 'vi');
                    }}
                  >
                    {v.label}
                  </button>
                ))
              )}
            </div>
            {cloned.length ? (
              <p style={{ ...hint, marginTop: 8 }}>
                Bạn có {cloned.length} giọng đã clone.{' '}
                <button
                  type="button"
                  style={{
                    ...secondaryBtn,
                    padding: '6px 10px',
                    minHeight: 32,
                    fontSize: 13,
                    display: 'inline-block',
                  }}
                  onClick={enterCloneMode}
                >
                  Chọn voice đã lưu
                </button>
              </p>
            ) : null}
          </>
        ) : (
          <>
            <p style={privacy}>
              Clone Voice chạy hoàn toàn trên máy bạn. Tiếng Việt dùng VieNeu-TTS (Apache-2.0);
              tiếng Anh dùng Chatterbox (MIT). Upload MP3/WAV 3–30 giây hoặc ghi micro.
            </p>
            {!consentOk && cloneModel.needsConsent ? (
              <p style={warnBox}>
                Nên tick xác nhận tải model ~{cloneModel.sizeMb}MB ở bước 1. Nút ghi âm luôn mở — bấm
                sẽ tự xác nhận và bắt đầu ghi.
              </p>
            ) : null}
            <p style={hint}>
              Bước: (1) chọn ngôn ngữ ở bước 3 → (2) xác nhận tải model → (3) đặt tên → (4) upload
              hoặc ghi âm → voice được lưu trên máy bạn.
            </p>
            <label style={label}>Tên giọng mới</label>
            <input
              type="text"
              value={cloneName}
              onChange={(e: any) => setCloneName(e.target.value)}
              placeholder="VD: Giọng của tôi"
              style={{ ...textArea, minHeight: 44, resize: 'none', marginBottom: 12 }}
            />
            <FileDropZone
              accept="audio/*,.mp3,.wav,.ogg,.m4a,.flac,.webm"
              label="Kéo thả file âm thanh hoặc bấm để chọn"
              hintText="MP3, WAV, OGG, M4A… · 5–30 giây, một người nói rõ"
              onFiles={onCloneFiles}
            />
            <div style={btnRow}>
              {recState !== 'recording' ? (
                <button
                  type="button"
                  style={{
                    ...secondaryBtn,
                    ...(narrow ? touchBtnFull : {}),
                  }}
                  onClick={startRec}
                >
                  Ghi âm micro
                </button>
              ) : (
                <button
                  type="button"
                  style={{ ...dangerBtn, ...(narrow ? touchBtnFull : {}) }}
                  onClick={stopRec}
                >
                  Dừng ({recElapsed}s / 30s)
                </button>
              )}
              {!cloneEngine.isReady(cloneModelId) ? (
                <button
                  type="button"
                  style={{
                    ...(busy ? btnDisabled(primaryBtn) : primaryBtn),
                    ...(narrow ? touchBtnFull : {}),
                  }}
                  disabled={busy}
                  onClick={loadModel}
                >
                  {diskCached ? 'Nạp model clone (đã có sẵn)' : 'Tải model clone trước'}
                </button>
              ) : (
                <span style={{ ...hint, margin: 0, alignSelf: 'center' }}>Model clone sẵn sàng</span>
              )}
            </div>

            {cloned.length ? (
              <>
                <label style={{ ...label, marginTop: 12 }}>Giọng đã lưu — chọn để TTS</label>
                {cloned.map((v) => (
                  <div
                    key={v.id}
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 8,
                      alignItems: 'center',
                      marginBottom: 8,
                      padding: '8px 10px',
                      background: selectedCloneId === v.id ? '#e3f2fd' : '#fafafa',
                      borderRadius: 8,
                      border: '1px solid #eceff1',
                    }}
                  >
                    <button
                      type="button"
                      style={chipStyle(selectedCloneId === v.id)}
                      onClick={() => {
                        setSelectedCloneId(v.id);
                        setModelId(v.modelId || cloneModelId);
                        if (v.lang === 'vi' || v.lang === 'en') setLangHint(v.lang as 'en' | 'vi');
                        setVoiceTab('clone');
                        setStatus('Đã chọn giọng: ' + v.name);
                      }}
                    >
                      {v.name}
                    </button>
                    <span style={{ ...hint, margin: 0, fontSize: 12 }}>
                      {v.engine} · {new Date(v.createdAt).toLocaleDateString()}
                      {v.metaRefPcm && v.metaRefPcm.length ? ' · có mẫu' : ' · thiếu mẫu'}
                    </span>
                    <button type="button" style={secondaryBtn} onClick={() => previewClone(v)}>
                      Nghe mẫu
                    </button>
                    <button type="button" style={dangerBtn} onClick={() => removeClone(v.id)}>
                      Xóa
                    </button>
                  </div>
                ))}
              </>
            ) : (
              <p style={hint}>Chưa có giọng clone nào — upload hoặc ghi âm để tạo.</p>
            )}
          </>
        )}
      </div>

      <div style={card}>
        <label style={label}>3. Nội dung</label>
        <div style={btnRow}>
          <button
            type="button"
            style={chipStyle(langHint === 'en')}
            onClick={() => {
              setLangHint('en');
              if (voiceTab === 'clone') setModelId('chatterbox-en');
            }}
          >
            English
          </button>
          <button
            type="button"
            style={chipStyle(langHint === 'vi')}
            onClick={() => {
              setLangHint('vi');
              if (voiceTab === 'clone') setModelId('vieneu-v3-turbo');
            }}
          >
            Tiếng Việt
          </button>
        </div>
        <textarea
          value={text}
          onChange={(e: any) => setText(e.target.value)}
          rows={5}
          style={textArea}
          placeholder="Nhập văn bản cần đọc…"
        />
        <label style={{ ...label, marginTop: 8 }}>Gợi ý nhanh</label>
        <div style={{ ...chipScrollRow, flexWrap: 'wrap', overflowX: 'visible' }}>
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              title={s}
              style={{
                ...chipStyle(text === s),
                whiteSpace: 'normal',
                textAlign: 'left',
                maxWidth: '100%',
                lineHeight: 1.35,
              }}
              onClick={() => setText(s)}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div style={card}>
        <label style={label}>4. Tốc độ &amp; cao độ</label>
        <div style={sliderRow}>
          <span style={sliderLabel}>Tốc độ {speed.toFixed(2)}×</span>
          <input
            type="range"
            min={0.5}
            max={2}
            step={0.05}
            value={speed}
            onChange={(e: any) => setSpeed(Number(e.target.value))}
            style={rangeInput}
          />
        </div>
        <div style={sliderRow}>
          <span style={sliderLabel}>Cao độ {pitch > 0 ? '+' : ''}{pitch.toFixed(1)}</span>
          <input
            type="range"
            min={-12}
            max={12}
            step={0.5}
            value={pitch}
            onChange={(e: any) => setPitch(Number(e.target.value))}
            style={rangeInput}
          />
        </div>
        {(voiceTab === 'clone' || (model.engine === 'chatterbox' && !model.comingSoon)) ? (
          <div style={sliderRow}>
            <span style={sliderLabel}>Cảm xúc {exaggeration.toFixed(2)}</span>
            <input
              type="range"
              min={0}
              max={1.5}
              step={0.05}
              value={exaggeration}
              onChange={(e: any) => setExaggeration(Number(e.target.value))}
              style={rangeInput}
            />
          </div>
        ) : null}
        <p style={hint}>Giống text-to-speech.online: tốc độ 0.5–2×, cao độ ±12 bán cung.</p>
      </div>

      <div style={card}>
        <label style={label}>5. Tạo audio</label>
        <p style={hint}>
          Nút chính bên dưới chuyển văn bản ở bước 3 thành âm thanh. Sau khi xong có thể tải WAV/MP3.
        </p>
        <div style={btnRow}>
          {!busy ? (
            <button
              type="button"
              style={{
                ...(!(text || '').trim() ||
                (voiceTab === 'clone' && !selectedCloneId) ||
                (voiceTab === 'preset' && model.comingSoon)
                  ? btnDisabled(primaryBtn)
                  : primaryBtn),
                ...(narrow ? touchBtnFull : { minWidth: 200, fontSize: 16, padding: '14px 20px' }),
              }}
              disabled={
                !(text || '').trim() ||
                (voiceTab === 'clone' && !selectedCloneId) ||
                (voiceTab === 'preset' && !!model.comingSoon)
              }
              onClick={doSynthesize}
            >
              {voiceTab === 'clone' ? 'Tạo audio (giọng clone)' : 'Tạo audio'}
            </button>
          ) : (
            <button
              type="button"
              style={{ ...dangerBtn, ...(narrow ? touchBtnFull : {}) }}
              onClick={cancelSynth}
            >
              Hủy tạo audio
            </button>
          )}
          {((voiceTab === 'clone' ? cloneEngine : engine).canExportFile) && lastPcm && lastPcm.length ? (
            <>
              <button
                type="button"
                style={{ ...(busy ? btnDisabled(secondaryBtn) : secondaryBtn), ...(narrow ? touchBtnFull : {}) }}
                disabled={busy}
                onClick={downloadWav}
              >
                Tải WAV
              </button>
              <button
                type="button"
                style={{ ...(busy ? btnDisabled(secondaryBtn) : secondaryBtn), ...(narrow ? touchBtnFull : {}) }}
                disabled={busy}
                onClick={downloadMp3}
              >
                Tải MP3
              </button>
            </>
          ) : null}
        </div>
        {status && busy ? <p style={{ ...hint, marginTop: 0 }}>{status}</p> : null}
        {!(voiceTab === 'clone' ? cloneEngine : engine).canExportFile ? (
          <p style={hint}>Giọng trình duyệt chỉ phát trực tiếp — không xuất được file WAV/MP3.</p>
        ) : null}
        {error ? <p style={errorBox}>{error}</p> : null}
        {audioUrl ? (
          <audio ref={audioRef} src={audioUrl} controls style={{ width: '100%', marginTop: 8 }} />
        ) : null}
      </div>
    </ToolLayout>
  );
};

export default TextToSpeech;
